import { FC } from "react";
import { ShiIcon } from "src/atoms";
import { asText } from "@prismicio/client";
import { useLocation } from "@reach/router";
import queryString from "query-string";
import * as DOMPurify from "isomorphic-dompurify";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  StackProps,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";

import { getColor } from "../utils";
import { parseOneLinerLogicalText, shouldShowLogicalText } from "../../utils";
import { LogicalText } from "../../types";
import { ClickInfoNewPricing } from "src/lib/event/types";
import { onClickNewAccountNewPricing } from "src/lib/event";
import isSignupURL from "src/atoms/PrismicLink/lib/isSignupURL";
import useVisitorContext from "src/providers/VisitorContext/useVisitorContext";
import handleWebAppRedirection from "src/atoms/PrismicLink/lib/handleWebAppRedirection";

const Card: FC<
  StackProps & { item: Gatsby.PageBlockPricingCardsItemFragment }
> = ({ item, ...rest }) => {
  if (!item) return null;

  const visitorContext = useVisitorContext();
  const location = useLocation();
  const { pathname, search } = location;

  const isMobile = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    navbar: true,
    lg: false,
    xl: false,
    "2xl": false,
    "3xl": false,
  });

  const currentParams = queryString.parse(search);
  const cycle = (currentParams["cycle"] as string) ?? "";
  const isMonthly = cycle === "mensuel";

  const shouldShow = (str: string) => {
    const output: LogicalText = parseOneLinerLogicalText(str);

    return shouldShowLogicalText(pathname, search, output);
  };

  const {
    is_displayed: isDisplayed,
    is_highlighted_bis: isHighlighted,
    badge_bis: badge,
    second_badge: secondBadge,
    badge_same_color: badgeSameColor,
    color,
    cta_is_secondary: ctaIsSecondary,
    cta_text_desktop_bis: ctaTextDesktop,
    cta_text_mobile_bis: ctaTextMobile,
    cta_text_desktop_monthly_to_yearly: ctaTextDesktopMonthlyToYearly,
    cta_text_mobile_monthly_to_yearly: ctaTextMobileMonthlyToYearly,
    cta_link_monthly: ctaLinkMonthly,
    cta_link_yearly: ctaLinkYearly,
    plan_name: planName,
    plan_subtitle: planSubtitle,
    price_subtitle_1: priceSubtitle1,
    price_subtitle_2: priceSubtitle2,
    price_value_bis: priceValue,
    price_text_suffix_bis: priceTextSuffix,
    multi_line_usp: multiLineUSP,
  } = item;

  const shouldShowCard = shouldShow(isDisplayed);
  if (!shouldShowCard) {
    return null;
  }

  const width = { base: "300px", md: "300px", xl: "320px", "2xl": "346px" };

  // TODO: rename renderPrice
  const renderPrice = (
    price: any,
    props = {},
    shouldSanitize = false,
    onlyText = false,
  ) => {
    const allTexts = asText(price.raw, "\n")?.split("\n");
    const matchingLogicalText = allTexts?.find((logicalText: string) => {
      const output: LogicalText = parseOneLinerLogicalText(logicalText);

      return shouldShowLogicalText(pathname, search, output);
    });

    if (matchingLogicalText) {
      if (shouldSanitize) {
        const canSanitize = DOMPurify.isSupported;
        if (!canSanitize) {
          return null;
        }
        const textSanitized = DOMPurify.sanitize(
          parseOneLinerLogicalText(matchingLogicalText).text || "",
          { USE_PROFILES: { html: true } },
        );
        return (
          <Box {...props} dangerouslySetInnerHTML={{ __html: textSanitized }} />
        );
      }

      if (onlyText) {
        return parseOneLinerLogicalText(matchingLogicalText).text;
      }

      return (
        <Box {...props}>
          {parseOneLinerLogicalText(matchingLogicalText).text}
        </Box>
      );
    }

    return null;
  };
  const priceTypoStyle = {
    fontFamily: "Shine Spectra",
    fontWeight: "600",
    fontStyle: "Demibold",
    fontSize: "30px",
    lineHeight: "30px",
    letterSpacing: "0%",
    color: shouldShow(isHighlighted) ? "white" : "text-primary"
  }

  // TODO: combine badges into one
  return (
    <VStack {...rest}>
      <VStack
        minWidth={width}
        maxWidth={width}
        bgColor={shouldShow(isHighlighted) ? "primary-green" : "secondary-stone-100"}
        borderRadius="12px 12px 0 0"
        color={shouldShow(isHighlighted) ? "white" : "text-primary"}
        //border opacity 20% when shouldShow(isHighlighted)
        borderBottom={shouldShow(isHighlighted) ? "1px solid #DCDCDC33" : "1px solid #DCDCDC"}
        paddingX={{ base: "space-16", md: "space-16", xl: "space-40" }}
        paddingTop={{ base: "space-32", md: "space-40" }}
        paddingBottom={{
          base: "space-16",
          md: isMonthly ? "space-40" : "space-32",
        }}
        position="relative"
      >
        {(badge?.raw?.length > 0 && renderPrice(badge)) ||
          (secondBadge?.raw?.length > 0 && renderPrice(secondBadge)) ||
          (badgeSameColor?.raw?.length > 0 && renderPrice(badgeSameColor)) ? (
          <HStack position="absolute" top="-18px" left="37px">
            {badge?.raw?.length > 0 && renderPrice(badge) ? (
              <HStack
                borderRadius="smIntermediate"
                paddingX="space-12"
                paddingY="space-8"
                fontSize="font-14"
                lineHeight={1.42}
                fontWeight="400"
                backgroundColor="#FCF0B6"
              >
                <ShiIcon name="sparkles" boxSize="14px" />
                <Text color="text-primary"
                >{renderPrice(badge)}</Text>
              </HStack>
            ) : null}
            {secondBadge?.raw?.length > 0 && renderPrice(secondBadge) ? (
              <HStack
                borderRadius="smIntermediate"
                paddingX="space-12"
                paddingY="space-8"
                fontSize="font-14"
                lineHeight={1.42}
                fontWeight="400"
                backgroundColor="grey-300"
                color="text-primary"
              >
                <ShiIcon
                  name="sparkle-single"
                  boxSize="14px"
                  marginTop="-2px"
                />
                <Text>{renderPrice(secondBadge)}</Text>
              </HStack>
            ) : null}
            {badgeSameColor?.raw?.length > 0 && renderPrice(badgeSameColor) ? (
              <HStack
                borderRadius="smIntermediate"
                paddingX="space-12"
                paddingY="space-8"
                fontSize="font-14"
                lineHeight={1.42}
                fontWeight="400"
                color={
                  ["btn-green-bg", "grey-0"].includes(getColor(color))
                    ? "white"
                    : "grey-0"
                }
                backgroundColor={getColor(color)}
              >
                <Text>{renderPrice(badgeSameColor)}</Text>
              </HStack>
            ) : null}
          </HStack>
        ) : (
          <HStack position="absolute" top="-18px" left="37px" />
        )}
        <Box>
          <HStack spacing="space-16">
            <Box

              textAlign="left"
            >
              {asText(planName.raw, "\n")
                ?.split("\n")
                .map((a: any) => {
                  // TODO: let's make this a util
                  if (!a) {
                    return null;
                  }

                  const output: LogicalText = parseOneLinerLogicalText(a);
                  if (shouldShowLogicalText(pathname, search, output)) {
                    return <Heading as="h3" key={output.Heading} variant="heading-3" color={shouldShow(isHighlighted) ? "white" : "text-primary"}>{output.text}</Heading>;
                  }

                  return null;
                })}
            </Box>
          </HStack>
          {renderPrice(planSubtitle, {
            fontSize: "font-15",
            lineHeight: "base",
            fontWeight: "normal",
            textAlign: "left",
            marginTop: "8px",
            marginBottom: { base: "space-12", md: "space-24" },
          })}
          <HStack>
            {renderPrice(priceValue, {
              ...priceTypoStyle,
            })}
            {renderPrice(priceTextSuffix, {
              ...priceTypoStyle,
            })}
          </HStack>
          {renderPrice(
            priceSubtitle1,
            {
              textAlign: "left",
              fontSize: "12px",
              marginTop: "space-4",
            },
            true,
          )}
          {renderPrice(priceSubtitle2, {
            textAlign: "left",
            color: "#56AA41",
            fontSize: "12px",
            paddingBottom: "space-8",
          })}
          <Box marginBottom="space-24" />
          <Button
            width="100%"
            marginTop="10px"
            variant={ctaIsSecondary ? "secondary" : shouldShow(isHighlighted) ? "primaryYellow" : "primary"}
            //disable hover effect
            _hover={{}}
            onClick={(e) => {
              const toUrlTemp = isMonthly
                ? ctaLinkMonthly?.url
                : ctaLinkYearly?.url;
              const clickInfoNewPricing: ClickInfoNewPricing = {
                label:
                  renderPrice(
                    isMobile ? ctaTextMobile : ctaTextDesktop,
                    {},
                    false,
                    true,
                  ) || "",
                platform: isMobile ? "web-mobile" : "web-desktop",
                block: "block_pricing_cards",
                fromLocation: location,
                plan: renderPrice(planName, {}, false, true),
                toUrl: isSignupURL(toUrlTemp)
                  ? handleWebAppRedirection(visitorContext, {
                    to: toUrlTemp,
                    from: location,
                  })
                  : toUrlTemp,
              };

              onClickNewAccountNewPricing(e, clickInfoNewPricing);
            }}
          >
            {renderPrice(isMobile ? ctaTextMobile : ctaTextDesktop)}
          </Button>
          {isMonthly &&
            renderPrice(
              isMobile
                ? ctaTextMobileMonthlyToYearly
                : ctaTextDesktopMonthlyToYearly,
            ) ? (
            <Button
              variant="tertiary"
              color={shouldShow(isHighlighted) ? "white" : "text-primary"}
              borderBottom="1px solid"
              marginTop="space-24"
              paddingBottom={{ base: "space-2", md: "space-2" }}
              //disable color change on hover effect
              _hover={{}}
              onClick={(e) => {
                const clickInfoNewPricing: ClickInfoNewPricing = {
                  label:
                    `${renderPrice(
                      isMobile
                        ? ctaTextMobileMonthlyToYearly
                        : ctaTextDesktopMonthlyToYearly,
                      {},
                      false,
                      true,
                    )} →` || "",
                  platform: isMobile ? "web-mobile" : "web-desktop",
                  block: "block_pricing_cards",
                  fromLocation: location,
                  plan: renderPrice(planName, {}, false, true),
                  toUrl: isSignupURL(ctaLinkMonthly?.url)
                    ? handleWebAppRedirection(visitorContext, {
                      to: ctaLinkMonthly?.url,
                      from: location,
                    })
                    : ctaLinkMonthly?.url,
                };

                onClickNewAccountNewPricing(e, clickInfoNewPricing);
              }}
            >
              {`${renderPrice(
                isMobile
                  ? ctaTextMobileMonthlyToYearly
                  : ctaTextDesktopMonthlyToYearly,
                {},
                false,
                true,
              )} →`} 
            </Button>
          ) : isMonthly ? (
            <Box height="40px"></Box>
          ) : null}
        </Box>
      </VStack>
      <VStack
        bgColor={shouldShow(isHighlighted) ? "primary-green" : "secondary-stone-100"}
        minWidth={width}
        maxWidth={width}
        minHeight={{ base: "280px", md: "330px" }} // TODO: improve this part for dynamic USP
        borderRadius="0 0 12px 12px"
        color={shouldShow(isHighlighted) ? "white" : "text-primary"}
        paddingX={{ base: "space-16", md: "space-16", xl: "space-40" }}
        paddingTop={{ base: "space-16", md: "space-32" }}
        paddingBottom={{ base: "space-16", md: "space-40" }}
        position="relative"
        marginTop="0px !important"
      >
        <Box
          // border="1px solid red"
          mt={{ base: "10px", md: 0 }}
        >
          <HStack >
            <Box>
              {asText(planName.raw, "\n")
                ?.split("\n")
                .map((a: any) => {
                  // TODO: let's make this a util
                  if (!a) {
                    return null;
                  }

                  const output: LogicalText = parseOneLinerLogicalText(a);
                  if (shouldShowLogicalText(pathname, search, output)) {
                    return (
                      <Text
                        key={output.text}
                        variant="body-sm-semibold"
                      >{`Avantages ${output.text}`}</Text>
                    );
                  }

                  return null;
                })}
            </Box>
          </HStack>
          <Box marginBottom="space-16" />
          <VStack alignItems="start" spacing="space-12">
            {asText(multiLineUSP.raw, "\n")
              ?.split("\n")
              .map((a: any) => {
                if (!a) {
                  return null;
                }

                const output: LogicalText = parseOneLinerLogicalText(a);
                if (shouldShowLogicalText(pathname, search, output)) {
                  return (
                    <Flex textAlign="left" key={output.text}>
                      <ShiIcon
                        name="check"
                        boxSize="14px"
                        marginTop="4px"
                      />
                      <Text
                        marginLeft="space-8"
                        fontSize="14px"
                        lineHeight="base"
                      >
                        {output.text}
                      </Text>
                    </Flex>
                  );
                }

                return null;
              })}
          </VStack>
        </Box>
      </VStack>
    </VStack >
  );
};

export default Card;
